/**
 * gdyniaPositions.js
 * 
 * Serwis aproksymacji pozycji GPS autobusów ZKM Gdynia.
 * 
 * Ponieważ API gpsPositions nie zwraca pojazdów gdyńskich,
 * szacujemy ich pozycję na podstawie:
 * 1. Estymowanych odjazdów (departures) — wiemy, jaki pojazd jedzie jaką trasą
 * 2. Rozkładu jazdy (stopTimes) — znamy sekwencję przystanków i czasy
 * 3. Współrzędnych przystanków — znamy lokalizację każdego przystanku
 */

import stopsData from '../data/stops.json';
import ZTM_API_BASE from './ztmApi';

const API_BASE = ZTM_API_BASE;

// ============================================================
// CACHE
// ============================================================

// Cache rozkładów jazdy: klucz = `${date}_${routeId}`, wartość = przetworzony rozkład
const scheduleCache = new Map();

// Cache kształtów tras: klucz = `${date}_${routeId}_${tripId}`, wartość = przetworzony shape
const shapeCache = new Map();

// Cache wspólrzędnych przystanków: stopId → {lat, lon}
let stopsCoordMap = null;

// Set ID gdyńskich przystanków (zoneName === "Gdynia")
let gdyniaStopIds = null;

// Zbiór vehicleId znanych z GPS (żeby nie duplikować)
let knownGpsVehicleIds = new Set();

// ============================================================
// INICJALIZACJA
// ============================================================

function getStopsCoordMap() {
  if (stopsCoordMap) return stopsCoordMap;
  stopsCoordMap = new Map();
  for (const stop of stopsData) {
    if (stop.stopLat && stop.stopLon) {
      stopsCoordMap.set(stop.stopId, { lat: stop.stopLat, lon: stop.stopLon });
    }
  }
  return stopsCoordMap;
}

function getGdyniaStopIds() {
  if (gdyniaStopIds) return gdyniaStopIds;
  gdyniaStopIds = new Set();
  for (const stop of stopsData) {
    // Przystanki gdyńskie mają stopId w zakresie 30000-49999
    if ((stop.stopId >= 30000 && stop.stopId < 50000) || stop.zoneName === 'Gdynia') {
      gdyniaStopIds.add(stop.stopId);
    }
  }
  return gdyniaStopIds;
}

// ============================================================
// POBIERANIE DANYCH
// ============================================================

/**
 * Pobiera masowo odjazdy dla całej sieci, ale filtruje je tylko dla gdyńskich przystanków.
 * Używamy endpointu, który zwraca JSON z obiektami pod kluczem każdego stopId.
 */
async function fetchDeparturesMulti() {
  const results = [];
  try {
    const res = await fetch(`${API_BASE}/departures`);
    const data = await res.json();
    const gdyniaStops = getGdyniaStopIds();

    for (const [stopIdStr, stopData] of Object.entries(data)) {
      if (stopIdStr === 'lastUpdate' || !gdyniaStops.has(Number(stopIdStr))) {
        continue;
      }
      if (stopData && stopData.departures && stopData.departures.length > 0) {
        const stopDeps = stopData.departures.map(d => ({ ...d, _fromStopId: Number(stopIdStr) }));
        results.push(...stopDeps);
      }
    }
  } catch (e) {
    console.error(`[GdyniaPos] Błąd masowego pobierania logów:`, e.message);
  }
  return results;
}

/**
 * Pobiera i cachuje rozkład jazdy (stopTimes) dla danej linii i dnia.
 * Zwraca mapę: kursKey → [{stopId, stopSequence, departureTime(Date)}]
 */
async function fetchTripSchedule(routeId, date) {
  const cacheKey = `${date}_${routeId}`;
  if (scheduleCache.has(cacheKey)) {
    return scheduleCache.get(cacheKey);
  }

  try {
    const res = await fetch(`${API_BASE}/stopTimes?date=${date}&routeId=${routeId}`);
    const data = await res.json();
    const stopTimes = data.stopTimes || [];

    // Grupuj po (tripId, busServiceName, order) — identyfikuje unikalny kurs
    const trips = new Map();
    for (const st of stopTimes) {
      const key = `${st.tripId}_${st.busServiceName}_${st.order}`;
      if (!trips.has(key)) {
        trips.set(key, []);
      }

      // Parsuj departureTime (format: 1899-12-30T06:15:00 lub 1899-12-31T...)
      const timePart = st.departureTime.split('T')[1]; // "06:15:00"
      const datePart = st.departureTime.split('T')[0]; // "1899-12-30" lub "1899-12-31"
      const isNextDay = datePart === '1899-12-31';

      trips.get(key).push({
        stopId: st.stopId,
        stopSequence: st.stopSequence,
        time: timePart,
        isNextDay,
        tripId: st.tripId,
        busServiceName: st.busServiceName,
      });
    }

    // Sortuj każdy kurs po stopSequence
    for (const [, stops] of trips) {
      stops.sort((a, b) => a.stopSequence - b.stopSequence);
    }

    scheduleCache.set(cacheKey, trips);
    return trips;
  } catch (e) {
    console.warn(`[GdyniaPos] Błąd pobierania rozkładu dla routeId=${routeId}:`, e.message);
    return new Map();
  }
}

// ============================================================
// SHAPES — pobieranie kształtów tras
// ============================================================

/**
 * Oblicza odległość euklidesową (w stopniach, wystarczająca do porównań).
 */
function distDeg(lat1, lon1, lat2, lon2) {
  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;
  return Math.sqrt(dLat * dLat + dLon * dLon);
}

/**
 * Pobiera i cachuje kształt trasy (GeoJSON LineString).
 * Zwraca przetworzony obiekt: { points: [{lat, lon}], cumDist: [number] }
 */
async function fetchRouteShape(routeId, tripId, date) {
  const cacheKey = `${date}_${routeId}_${tripId}`;
  if (shapeCache.has(cacheKey)) {
    return shapeCache.get(cacheKey);
  }

  try {
    const res = await fetch(`${API_BASE}/shapes?date=${date}&routeId=${routeId}&tripId=${tripId}`);
    const data = await res.json();
    const coords = data.coordinates || [];

    if (coords.length < 2) {
      shapeCache.set(cacheKey, null);
      return null;
    }

    // GeoJSON: [lon, lat] → konwertuj na {lat, lon}
    const points = coords.map(c => ({ lat: c[1], lon: c[0] }));

    // Oblicz kumulatywne odległości (w stopniach — wystarczy do proporcji)
    const cumDist = [0];
    for (let i = 1; i < points.length; i++) {
      const d = distDeg(points[i - 1].lat, points[i - 1].lon, points[i].lat, points[i].lon);
      cumDist.push(cumDist[i - 1] + d);
    }

    const shape = { points, cumDist, totalDist: cumDist[cumDist.length - 1] };
    shapeCache.set(cacheKey, shape);
    return shape;
  } catch (e) {
    console.warn(`[GdyniaPos] Błąd shapes routeId=${routeId}, tripId=${tripId}:`, e.message);
    shapeCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Znajduje indeks najbliższego punktu w shape dla danej współrzędnej.
 */
function findNearestShapeIndex(shape, lat, lon) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < shape.points.length; i++) {
    const d = distDeg(lat, lon, shape.points[i].lat, shape.points[i].lon);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Interpoluje pozycję wzdłuż shape'a na podstawie kumulatywnej odległości.
 * targetDist = odległość od początku shape'a, na której ma być punkt.
 */
function interpolateAlongShape(shape, targetDist) {
  // Clamp
  if (targetDist <= 0) return shape.points[0];
  if (targetDist >= shape.totalDist) return shape.points[shape.points.length - 1];

  // Znajdź segment
  for (let i = 0; i < shape.cumDist.length - 1; i++) {
    if (targetDist >= shape.cumDist[i] && targetDist <= shape.cumDist[i + 1]) {
      const segLen = shape.cumDist[i + 1] - shape.cumDist[i];
      const t = segLen > 0 ? (targetDist - shape.cumDist[i]) / segLen : 0;
      return {
        lat: shape.points[i].lat + (shape.points[i + 1].lat - shape.points[i].lat) * t,
        lon: shape.points[i].lon + (shape.points[i + 1].lon - shape.points[i].lon) * t,
      };
    }
  }
  return shape.points[shape.points.length - 1];
}

// ============================================================
// LOGIKA APROKSYMACJI
// ============================================================

/**
 * Konwertuje czas rozkładowy "HH:MM:SS" + flaga isNextDay na minuty od północy.
 */
function timeToMinutes(timeStr, isNextDay) {
  const [h, m, s] = timeStr.split(':').map(Number);
  return h * 60 + m + (s || 0) / 60 + (isNextDay ? 24 * 60 : 0);
}

/**
 * Zwraca aktualny czas jako minuty od północy (czas lokalny).
 */
function nowAsMinutes() {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
}

/**
 * Interpoluje liniowo między dwoma punktami (fallback).
 */
function lerp(a, b, t) {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

/**
 * Dla danego kursu i aktualnego czasu, oblicz przybliżoną pozycję pojazdu.
 * Jeśli dostępny jest kształt trasy (shape), interpoluje wzdłuż niego.
 * W przeciwnym razie — fallback na interpolację liniową.
 */
function interpolatePosition(tripStops, delayMinutes, coordsMap, nowMin, shape) {
  if (!tripStops || tripStops.length < 2) return null;

  // Dla każdego przystanku oblicz "realny" czas (rozkładowy + opóźnienie)
  const stopsWithRealTime = tripStops.map(s => ({
    ...s,
    realTimeMin: timeToMinutes(s.time, s.isNextDay) + delayMinutes,
  }));

  const firstStop = stopsWithRealTime[0];
  const lastStop = stopsWithRealTime[stopsWithRealTime.length - 1];

  // LOGIKA KURSÓW NOCNYCH (po północy):
  // Systemy transportowe często przypisują kursy nocne do "doby biznesowej",
  // przez co ich czas potrafi wynosić 24h, 25h, 26h (np. 25:20 = 1520 minut).
  // Jeśli nasz rzeczywisty czas to wczesny ranek (np. 01:30 = 90 minut),
  // a rozkład kursu z API wciąż używa notacji >24h, lub zaczynał się wieczorem,
  // musimy zrównać nasz czas lokalny z logiką ZTM (dodać 1440 minut - 24h).
  let adjustedNowMin = nowMin;
  if (nowMin <= 240 && lastStop.realTimeMin >= 1200) {
    adjustedNowMin = nowMin + 1440;
  }

  // Autobus jest PRZED pierwszym przystankiem (do 10 min wcześniej)
  if (adjustedNowMin < firstStop.realTimeMin && adjustedNowMin >= firstStop.realTimeMin - 10) {
    const coord = shape ? shape.points[0] : coordsMap.get(firstStop.stopId);
    if (coord) {
      return { lat: coord.lat, lon: coord.lon, prevStopId: firstStop.stopId, nextStopId: firstStop.stopId, progress: 0 };
    }
  }

  // Autobus jest PO ostatnim przystanku (do 3 min po)
  if (adjustedNowMin > lastStop.realTimeMin && adjustedNowMin <= lastStop.realTimeMin + 3) {
    const coord = shape ? shape.points[shape.points.length - 1] : coordsMap.get(lastStop.stopId);
    if (coord) {
      return { lat: coord.lat, lon: coord.lon, prevStopId: lastStop.stopId, nextStopId: lastStop.stopId, progress: 1 };
    }
  }

  // Znajdź odcinek, na którym teraz jest pojazd
  for (let i = 0; i < stopsWithRealTime.length - 1; i++) {
    const current = stopsWithRealTime[i];
    const next = stopsWithRealTime[i + 1];

    if (adjustedNowMin >= current.realTimeMin && adjustedNowMin <= next.realTimeMin) {
      const segmentDuration = next.realTimeMin - current.realTimeMin;
      const progress = segmentDuration > 0
        ? (adjustedNowMin - current.realTimeMin) / segmentDuration
        : 0;

      // === INTERPOLACJA WZDŁUŻ SHAPE'A ===
      if (shape) {
        const coordA = coordsMap.get(current.stopId);
        const coordB = coordsMap.get(next.stopId);
        if (coordA && coordB) {
          const idxA = findNearestShapeIndex(shape, coordA.lat, coordA.lon);
          const idxB = findNearestShapeIndex(shape, coordB.lat, coordB.lon);
          const distA = shape.cumDist[idxA];
          const distB = shape.cumDist[idxB];
          const targetDist = distA + (distB - distA) * progress;
          const pos = interpolateAlongShape(shape, targetDist);
          return {
            lat: pos.lat, lon: pos.lon,
            prevStopId: current.stopId, nextStopId: next.stopId, progress,
          };
        }
      }

      // === FALLBACK: interpolacja liniowa ===
      const coordA = coordsMap.get(current.stopId);
      const coordB = coordsMap.get(next.stopId);
      if (!coordA || !coordB) continue;

      return {
        lat: lerp(coordA.lat, coordB.lat, progress),
        lon: lerp(coordA.lon, coordB.lon, progress),
        prevStopId: current.stopId, nextStopId: next.stopId, progress,
      };
    }
  }

  return null;
}

// ============================================================
// GŁÓWNA FUNKCJA EKSPORTOWANA
// ============================================================

/**
 * Aktualizuje zbiór vehicleId widocznych w normalnym GPS.
 * Wywoływać z useVehicles co odświeżenie.
 */
export function setKnownGpsVehicleIds(vehicleIds) {
  knownGpsVehicleIds = new Set(vehicleIds);
}

/**
 * Pobiera aproksymowane pozycje pojazdów gdyńskich w całym mieście.
 * 
 * @returns {Promise<Array>} - tablica obiektów z przybliżoną pozycją
 */
export async function getApproximatedGdyniaPositions() {
  const coordsMap = getStopsCoordMap();
  const today = new Date().toISOString().split('T')[0];
  const nowMin = nowAsMinutes();

  console.log(`[GdyniaPos] Start: pobieranie z całego miasta, data=${today}, nowMin=${nowMin.toFixed(1)} (${Math.floor(nowMin/60)}:${String(Math.floor(nowMin%60)).padStart(2,'0')})`);

  // 1. Pobierz masowo odjazdy z całego miasta (z odfiltrowaniem gdyńskich wewnątrz)
  const allDepartures = await fetchDeparturesMulti();
  console.log(`[GdyniaPos] Pobrano ${allDepartures.length} odjazdów łącznie`);

  // 2. Filtruj: tylko pojazdy z REALTIME status i z vehicleId (aktywne kursy)
  //    Wyklucz pojazdy już widoczne w normalnym GPS
  const activeDepartures = allDepartures.filter(d =>
    d.status === 'REALTIME' &&
    d.vehicleId &&
    !knownGpsVehicleIds.has(d.vehicleId)
  );
  console.log(`[GdyniaPos] Po filtracji (REALTIME + !GPS): ${activeDepartures.length} odjazdów (wykluczone GPS: ${knownGpsVehicleIds.size})`);

  // 3. Grupuj po vehicleId — jeden pojazd może pojawiać się na wielu przystankach
  //    Bierzemy ten z najpóźniejszym timestamp (najświeższa informacja)
  const vehicleMap = new Map();
  for (const dep of activeDepartures) {
    const existing = vehicleMap.get(dep.vehicleId);
    if (!existing || dep.timestamp > existing.timestamp) {
      vehicleMap.set(dep.vehicleId, dep);
    }
  }
  console.log(`[GdyniaPos] Unikalne pojazdy: ${vehicleMap.size}`);

  if (vehicleMap.size > 0) {
    // Pokaż pierwszy przykładowy pojazd
    const sample = [...vehicleMap.values()][0];
    console.log(`[GdyniaPos] Przykład: linia ${sample.routeShortName}, tripId=${sample.tripId}, vehicleService=${sample.vehicleService}, vehicleId=${sample.vehicleId}`);
  }

  // 4. Dla każdego unikalnego pojazdu — pobierz rozkład i interpoluj
  const routeIds = [...new Set([...vehicleMap.values()].map(d => d.routeId))];
  console.log(`[GdyniaPos] Pobieranie rozkładów dla ${routeIds.length} linii: ${routeIds.join(', ')}`);

  // Pobierz rozkłady równolegle (cache zadziała dla powtórzeń)
  await Promise.all(routeIds.map(rid => fetchTripSchedule(rid, today)));

  // 5. Pobierz kształty tras (shapes) dla unikalnych (routeId, tripId)
  const tripShapeKeys = [...new Set(
    [...vehicleMap.values()].map(d => `${d.routeId}_${d.tripId}`)
  )];
  await Promise.all(
    tripShapeKeys.map(key => {
      const [routeId, tripId] = key.split('_').map(Number);
      return fetchRouteShape(routeId, tripId, today);
    })
  );
  console.log(`[GdyniaPos] Pobrano shapes dla ${tripShapeKeys.length} tras`);

  const approximatedVehicles = [];
  let matchCount = 0;
  let noMatchCount = 0;
  let noPositionCount = 0;

  for (const [vehicleId, dep] of vehicleMap) {
    const schedule = scheduleCache.get(`${today}_${dep.routeId}`);
    if (!schedule) {
      noMatchCount++;
      continue;
    }

    // Znajdź kurs tego pojazdu w rozkładzie — wybierz najlepszy czasowo
    let matchedTrip = null;
    let matchMethod = '';
    const delayMinutes = (dep.delayInSeconds || 0) / 60;

    // Zbierz WSZYSTKICH kandydatów (zamiast brać pierwszego trafionego)
    const candidates = [];

    for (const [key, tripStops] of schedule) {
      const keyParts = key.split('_');
      const keyTripId = parseInt(keyParts[0]);
      const keyServiceName = keyParts[1];

      // Match criteria: tripId musi się zgadzać
      if (keyTripId !== dep.tripId) continue;

      // Preferuj vehicleService match, ale zbieraj też inne
      const serviceMatch = dep.vehicleService && keyServiceName === dep.vehicleService;

      if (tripStops.length < 2) continue;

      // Oblicz zakres czasowy kursu z uwzględnieniem opóźnienia
      const sortedStops = [...tripStops].sort((a, b) => a.stopSequence - b.stopSequence);
      const firstMin = timeToMinutes(sortedStops[0].time, sortedStops[0].isNextDay) + delayMinutes;
      const lastMin = timeToMinutes(sortedStops[sortedStops.length - 1].time, sortedStops[sortedStops.length - 1].isNextDay) + delayMinutes;

      // Adjust for midnight crossing
      let adjNow = nowMin;
      if (nowMin <= 240 && lastMin >= 1200) {
        adjNow = nowMin + 1440;
      }

      // Oblicz "odległość" od aktualnego czasu
      let diff;
      if (adjNow >= firstMin - 10 && adjNow <= lastMin + 3) {
        diff = 0; // Idealnie — teraz trwa ten kurs
      } else if (adjNow < firstMin - 10) {
        diff = (firstMin - 10) - adjNow; // Przyszły kurs
      } else {
        diff = adjNow - (lastMin + 3); // Zakończony kurs
      }

      candidates.push({
        tripStops: sortedStops,
        serviceMatch,
        diff,
        method: serviceMatch ? 'tripId+service' : 'tripId-only',
      });
    }

    // Sortuj: najpierw po serviceMatch (desc), potem po diff (asc)
    candidates.sort((a, b) => {
      if (a.serviceMatch !== b.serviceMatch) return b.serviceMatch - a.serviceMatch;
      return a.diff - b.diff;
    });

    if (candidates.length > 0) {
      matchedTrip = candidates[0].tripStops;
      matchMethod = candidates[0].method;
    }

    // Fallback: Method 2 — tripId + scheduledTripStartTime (jeśli żaden kandydat nie pasował)
    if (!matchedTrip) {
      const depStartTime = dep.scheduledTripStartTime;
      if (depStartTime) {
        const utcDate = new Date(depStartTime);
        const localHours = String(utcDate.getHours()).padStart(2, '0');
        const localMins = String(utcDate.getMinutes()).padStart(2, '0');
        const depStartTimePart = `${localHours}:${localMins}`;
        for (const [key, tripStops] of schedule) {
          const keyTripId = parseInt(key.split('_')[0]);
          if (keyTripId === dep.tripId && tripStops.length > 0) {
            const firstStopTime = tripStops[0].time.substring(0, 5);
            if (firstStopTime === depStartTimePart) {
              matchedTrip = tripStops;
              matchMethod = 'tripId+startTime';
              break;
            }
          }
        }
      }
    }

    if (!matchedTrip) {
      noMatchCount++;
      continue;
    }

    matchCount++;

    // Pobierz shape dla tego kursu (z cache)
    const shape = shapeCache.get(`${today}_${dep.routeId}_${dep.tripId}`) || null;
    const position = interpolatePosition(matchedTrip, delayMinutes, coordsMap, nowMin, shape);

    if (position) {
      approximatedVehicles.push({
        vehicleId,
        vehicleCode: dep.vehicleCode,
        lat: position.lat,
        lon: position.lon,
        routeShortName: dep.routeShortName,
        headsign: dep.headsign,
        tripId: dep.tripId,
        routeId: dep.routeId,
        delay: dep.delayInSeconds || 0,
        speed: null,
        direction: null,
        isApproximated: true,
        progress: position.progress,
        vehicleService: dep.vehicleService,
      });
    } else {
      noPositionCount++;
      // Debug: pokaż zakres czasu kursu
      if (matchedTrip.length >= 2) {
        const firstTime = timeToMinutes(matchedTrip[0].time, matchedTrip[0].isNextDay) + delayMinutes;
        const lastTime = timeToMinutes(matchedTrip[matchedTrip.length-1].time, matchedTrip[matchedTrip.length-1].isNextDay) + delayMinutes;
        console.log(`[GdyniaPos] Brak pozycji dla ${dep.routeShortName} (${matchMethod}): kurs ${firstTime.toFixed(0)}-${lastTime.toFixed(0)} min, teraz=${nowMin.toFixed(0)} min`);
      }
    }
  }

  console.log(`[GdyniaPos] Wynik: ${approximatedVehicles.length} markerów (matched=${matchCount}, noMatch=${noMatchCount}, noPosition=${noPositionCount})`);

  return approximatedVehicles;
}

/**
 * Filtruje listę stopId do tych, które należą do Gdyni.
 */
export function filterGdyniaStops(stopIds) {
  const gdyniaIds = getGdyniaStopIds();
  return stopIds.filter(id => gdyniaIds.has(id));
}

/**
 * Zwraca ID przystanków gdyńskich z załadowanych danych.
 */
export function getAllGdyniaStopIds() {
  return [...getGdyniaStopIds()];
}
